import { type CSSProperties, type ReactNode } from "react";
import { Link } from "~/components/Link";
import { cn } from "~/lib/utils";
import { Routes } from "~/routes";

// // // //

/** Webflow gray scale from rwa.xyz marketing CSS (archived Jan 2026). */
const WF_GRAY_300 = "#e4e4e7";
const WF_GRAY_600 = "#696977";
const WF_GRAY_700 = "#4d4d56";

const STATS = [
    { value: "$21B+", label: "Tokenized AUM coverage" },
    { value: "15+", label: "Covered blockchain networks" },
    { value: "240+", label: "Covered issuers" },
    { value: "25M", label: "Individual transactions" },
] as const;

const BACKERS = [
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/652dd98ed61d9c674926ecf7_Extended-Black.svg",
        alt: "Archetype",
        width: 111,
        className: "opacity-[0.63]",
    },
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/6661d0a07cd725743bb020df_image%2084.png",
        alt: "Modular Capital",
        width: 150,
        className: "opacity-80",
    },
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/656f428898db2a897abb0235_DeptOfXYZ-Logo.png",
        alt: "Dept of XYZ",
        width: 59,
        className: "opacity-[0.63]",
    },
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/6577c8595285b8627730bbb8_jump-logo-final.png",
        alt: "Jump",
        width: 120,
        className: "opacity-80",
    },
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/65776a439d74df718bd28c85_shima-capital.png",
        alt: "Shima Capital",
        width: 160,
        className: "opacity-80",
    },
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/652ddc2721178e34c2113297_6075ce0896a9d3d86d400162_Image-1-p-500.png",
        alt: "Gatecap Ventures",
        width: 190,
        className: "opacity-[0.81]",
    },
    {
        src: "https://cdn.prod.website-files.com/6470c67dbb66a109d9a2e80b/6577d22ae44086645ca02ef4_nascent-logo-final.png",
        alt: "Nascent",
        width: 120,
        className: "opacity-80",
    },
] as const;

const SECTION_X =
    "px-2.5 max-[479px]:px-2.5 sm:px-6 lg:px-[75px] max-[479px]:py-10 py-16";

interface AboutStatProps {
    value: string;
    label: string;
    showDivider: boolean;
}

/** Single metric in the coverage stats bar. */
function AboutStat(props: AboutStatProps) {
    const { value, label, showDivider } = props;

    return (
        <>
            {showDivider ? (
                <div
                    aria-hidden
                    className={cn(
                        "shrink-0 bg-(--wf-stat-divider)",
                        "max-lg:h-px max-lg:w-4/5 max-lg:mx-auto",
                        "lg:h-32 lg:w-px",
                    )}
                    style={
                        {
                            "--wf-stat-divider": WF_GRAY_300,
                        } as CSSProperties
                    }
                />
            ) : null}
            <div className="flex w-full flex-1 flex-col items-center justify-center px-8 py-8 text-center">
                <h3 className="mb-2 text-[56px] font-medium leading-none tracking-[-0.02em] max-md:text-[48px] max-[479px]:text-[44px]">
                    {value}
                </h3>
                <p
                    className="mb-0 text-[21px] font-light leading-[1.8] tracking-[-0.015em]"
                    style={{ color: WF_GRAY_700 }}
                >
                    {label}
                </p>
            </div>
        </>
    );
}

interface AboutContentSectionProps {
    eyebrow: string;
    title: string;
    children: ReactNode;
    isLast?: boolean;
}

/**
 * Two-column block matching Webflow `.about-text-wrapper` layout from rwa.xyz/about.
 */
function AboutContentSection(props: AboutContentSectionProps) {
    const { eyebrow, title, children, isLast = false } = props;

    return (
        <section
            className={cn(
                "mx-auto grid max-w-[1280px] grid-cols-1 gap-4 md:grid-cols-2 md:gap-4",
                "border-b border-black/8 pb-[60px]",
                { "mb-[60px]": !isLast },
            )}
        >
            <p className="font-sans text-[30px] font-normal leading-none text-foreground md:col-start-1 md:row-start-1">
                {eyebrow}
            </p>
            <h4
                className={cn(
                    "font-sans text-[40px] font-medium leading-[1.2] tracking-[-0.03em] text-foreground",
                    "pr-5 max-md:text-4xl md:col-start-1 md:row-start-2",
                )}
            >
                {title}
            </h4>
            <div
                className={cn(
                    "space-y-4 font-sans text-[20px] font-normal leading-[1.3] tracking-normal text-foreground",
                    "md:col-start-2 md:row-start-1 md:row-span-2 md:self-start",
                )}
            >
                {children}
            </div>
        </section>
    );
}

/** About Us marketing page (styles matched to archived rwa.xyz/about Webflow CSS). */
export function AboutPage() {
    return (
        <div className="font-sans antialiased text-foreground [-webkit-font-smoothing:antialiased]">
            <section className={SECTION_X}>
                <div className="w-full">
                    <p className="mb-2.5 text-lg text-foreground">About Us</p>
                    <h1 className="mt-0 mb-0 text-[90px] font-medium leading-none tracking-[-0.04em] max-md:text-[58px]">
                        The Data Index for Internet Capital Markets
                    </h1>
                    <div className="mt-5 max-w-[900px] space-y-4 text-xl leading-normal tracking-normal">
                        <p>
                            At RWA.xyz, we are hyper-focused on delivering the
                            clearest insights into the tokenized real-world
                            asset (RWA) market.
                        </p>
                        <p>
                            Our platform aggregates and standardizes first-party
                            data to provide an accurate and intuitive market
                            intelligence experience for investors, protocols,
                            regulators, and service providers. We are a New York
                            City-based company.
                        </p>
                    </div>
                </div>
            </section>

            <section className={SECTION_X} aria-label="Coverage statistics">
                <div
                    className={cn(
                        "flex w-full items-center justify-between overflow-hidden rounded-xl border",
                        "max-lg:flex-col max-lg:gap-0",
                    )}
                    style={{ borderColor: WF_GRAY_300 }}
                >
                    {STATS.map((stat, index) => (
                        <AboutStat
                            key={stat.label}
                            value={stat.value}
                            label={stat.label}
                            showDivider={index > 0}
                        />
                    ))}
                </div>
            </section>

            <section id="Content" className={SECTION_X}>
                <AboutContentSection
                    eyebrow="Our mission"
                    title="Connect internet capital market participants"
                >
                    <p>
                        The next generation of financial markets are being built
                        on global and permissionless distributed ledgers.
                    </p>
                    <p>
                        Our mission is to accelerate the flow of capital between
                        participants by providing the best market intelligence
                        and distribution platform for investors, issuers, and
                        blockchains.
                    </p>
                </AboutContentSection>

                <AboutContentSection
                    eyebrow="What we do"
                    title="Provide source-of-truth market intelligence and distribution"
                    isLast
                >
                    <p>
                        We aggregate and standardize tokenized real-world asset
                        data across a fragmented ecosystem, from different
                        blockchains to token standards to asset classes.
                    </p>
                    <p>
                        By partnering directly with asset issuers and
                        systematically verifying data, we are able to provide an
                        accurate and unified source-of-truth for the
                        tokenization industry.
                    </p>
                    <p>
                        Our data platform is used by the following companies to
                        power insights: JP Morgan, S&amp;P Global, International
                        Monetary Fund, Moody&apos;s, DTCC, US Treasury
                        Department, Fidelity, Boston Consulting Group, WEF,
                        Citibank, Galaxy, McKinsey, Bloomberg, Rothschild Bank,
                        Visa, and more. See our{" "}
                        <Link
                            href={Routes.citations}
                            analyticsLabel="AboutPage - Citations Link Click"
                            className="text-[#ff8d36] no-underline hover:opacity-90"
                        >
                            citations
                        </Link>{" "}
                        for an extensive list of reference content.
                    </p>
                </AboutContentSection>
            </section>

            <section
                aria-labelledby="about-backed-by"
                className={cn(
                    "relative border-b px-2.5 pt-5 pb-[60px] sm:px-6 lg:px-[75px]",
                    "max-md:px-4 max-md:py-[60px]",
                )}
                style={{ borderColor: "#e4ebf3" }}
            >
                <p
                    id="about-backed-by"
                    className="mb-8 text-center text-base leading-5 font-light"
                    style={{ color: WF_GRAY_600 }}
                >
                    <strong className="font-semibold">Backed by</strong>
                </p>
                <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-around gap-2.5">
                    {BACKERS.map((backer) => (
                        <img
                            key={backer.alt}
                            src={backer.src}
                            alt={backer.alt}
                            width={backer.width}
                            loading="lazy"
                            className={cn(
                                "mb-2.5 h-auto max-h-9 w-auto max-w-[60%] object-contain",
                                "mx-[15px] bg-transparent",
                                backer.className,
                            )}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}
